import { useDispatch, useSelector } from 'react-redux';
import {
  setListing,
  setSelected,
  setFilters,
  setSelectedFilters,
  setNextToken,
} from '../store/ducks/reservation';
import { HeadCell } from '../models/dataTable';
import useApp from './useApp';
import {
  CreateVariables,
  ConceptGetVariables,
  ConceptListingVariables,
  Option,
} from '../models/app';
import {
  getReservation,
  listReservations,
  reservationsByConceptId,
} from '../graphql/queries';
import {
  createReservation,
  updateReservation,
  deleteReservation,
} from '../graphql/mutations';
import { generateClient } from '@aws-amplify/api';
import { ModelReservationFilterInput } from '../API';

const client = generateClient();

const useReservation = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();

  // const generalFilters = useSelector((state: any) => state.generalFilters);

  const listing = useSelector(
    (state: any) => state[`${listingName}`]?.['listing'] || [],
  );
  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]?.['nextToken'] || null,
  );

  async function fetch(params: ConceptListingVariables, isAuth = true) {
    const { limit, searchText, fromDate, toDate, conceptID, conceptsSelectedFilters } = params;

    try {
      let or: Array<ModelReservationFilterInput | null> | null = [];
      let and: Array<ModelReservationFilterInput | null> | null = [];
      const filter: any = {
        deleted: { eq: '0' },
      };

      // Build search text filters
      const searchFilters: Array<ModelReservationFilterInput | null> = [];
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        searchFilters.push({ guestName: { contains: searchLower } });
        searchFilters.push({ guestPhoneNumber: { contains: searchLower } });
        searchFilters.push({ confirmationNumber: { contains: searchLower } });
      }

      // Build concept filters
      const conceptFilters: Array<ModelReservationFilterInput | null> = [];
      if (conceptsSelectedFilters && conceptsSelectedFilters.length > 0) {
        // Multiple concepts selected
        conceptFilters.push(...conceptsSelectedFilters.map((conceptId: string) => ({
          conceptId: { eq: conceptId },
        })));
      } else if (conceptID) {
        // Single concept selected
        conceptFilters.push({ conceptId: { eq: conceptID } });
      }

      // Combine filters properly
      if (conceptFilters.length > 0 && searchFilters.length > 0) {
        // Both concept and search filters: use 'and' to combine them
        filter.and = [
          { or: conceptFilters },
          { or: searchFilters },
        ];
      } else if (conceptFilters.length > 0) {
        // Only concept filters
        if (conceptFilters.length === 1) {
          // Single concept, use direct filter
          filter.conceptId = { eq: conceptsSelectedFilters?.[0] || conceptID };
        } else {
          // Multiple concepts, use 'or'
          filter.or = conceptFilters;
        }
      } else if (searchFilters.length > 0) {
        // Only search filters
        filter.or = searchFilters;
      }

      // Date Filter - must be combined with existing filters
      if (fromDate && toDate) {
        const dateFilter = {
          createdAt: {
            between: [fromDate, toDate],
          },
        };

        // If 'and' filter already exists, add date filter to it
        if (filter.and) {
          filter.and.push(dateFilter);
        } else if (filter.or) {
          // If only 'or' exists, combine it with 'createdAt' using 'and'
          filter.and = [{ or: filter.or }, dateFilter];
          delete filter.or;
        } else {
          // No complex filters, add date filter directly
          filter.createdAt = dateFilter.createdAt;
        }
      }

      const reservationListing: any = await client.graphql({
        query: listReservations,
        variables: {
          filter,
          limit: 100000,
        },
        authMode: isAuth ? 'userPool' : 'iam',
      });

      const currentNextToken =
        reservationListing.data.listReservations.nextToken;
      const listing: any = reservationListing.data.listReservations.items;

      dispatch(setNextToken(currentNextToken));
      dispatch(setListing(listing));
      return listing;
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message || err);
      return [];
    }
  }

  async function fetchAll(params: ConceptListingVariables, isAuth = true) {
    const { searchText, fromDate, toDate, conceptID } = params;

    try {
      let or: Array<ModelReservationFilterInput | null> | null = [];
      let and: Array<ModelReservationFilterInput | null> | null = [];
      const filter: any = {
        deleted: { eq: '0' },
      };

      if (searchText) {
        or.push({ guestName: { contains: searchText.toLowerCase() } });
        or.push({ guestPhoneNumber: { contains: searchText.toLowerCase() } });
        or.push({ confirmationNumber: { contains: searchText.toLowerCase() } });
      }

      if (conceptID) {
        filter.conceptId = { eq: conceptID };
      }

      if (or.length > 0) {
        filter.or = or;
      }

      // Date Filter - must be combined with existing filters
      if (fromDate && toDate) {
        const dateFilter = {
          createdAt: {
            between: [fromDate, toDate],
          },
        };

        // If 'and' filter already exists, add date filter to it
        if (filter.and) {
          filter.and.push(dateFilter);
        } else if (filter.or) {
          // If only 'or' exists, combine it with 'createdAt' using 'and'
          filter.and = [{ or: filter.or }, dateFilter];
          delete filter.or;
        } else {
          // No complex filters, add date filter directly
          filter.createdAt = dateFilter.createdAt;
        }
      }

      let allItems: any[] = [];
      let nextToken: string | null = null;

      do {
        const response: any = await client.graphql({
          query: listReservations,
          variables: {
            filter,
            limit: 1000, // Amplify safe limit
            nextToken,
          },
          authMode: isAuth ? 'userPool' : 'iam',
        });

        const result = response.data.listReservations;

        allItems = [...allItems, ...(result.items || [])];
        nextToken = result.nextToken ?? null;
      } while (nextToken);

      dispatch(setListing(allItems));
      return allItems;
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message || err);
      return [];
    }
  }

  async function fetchByConept(params: ConceptListingVariables, isAuth = true) {
    const { limit, searchText, fromDate, toDate, conceptID } = params;

    try {
      /* Validations */
      if (conceptID === undefined || conceptID === '') {
        showError('Invalid concept ID.');
        return [];
      }

      /* Filters */
      let or: Array<ModelReservationFilterInput | null> | null = [];
      let and: Array<ModelReservationFilterInput | null> | null = [];
      const filter: any = {
        deleted: { eq: '0' },
      };

      if (searchText) {
        or.push({ guestName: { contains: searchText.toLowerCase() } });
        or.push({ guestPhoneNumber: { contains: searchText.toLowerCase() } });
        or.push({ confirmationNumber: { contains: searchText.toLowerCase() } });
      }

      if (or.length > 0) {
        filter.or = or;
      }

      // Date Filter
      let dateFilter = {};
      if (fromDate && toDate) {
        dateFilter = {
          between: [fromDate, toDate],
        };
      }

      const reservationListing: any = await client.graphql({
        query: reservationsByConceptId,
        variables: {
          conceptId: conceptID,
          createdAt: dateFilter,
          filter,
          limit: limit ?? 100,
        },
        authMode: isAuth ? 'userPool' : 'iam',
      });

      const currentNextToken =
        reservationListing.data.reservationsByConceptId.nextToken;
      const listing: any =
        reservationListing.data.reservationsByConceptId.items;

      dispatch(setNextToken(currentNextToken));
      dispatch(setListing(listing));
      return listing;
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message || err);
      return [];
    }
  }

  async function fetchAllByConept(
    params: ConceptListingVariables,
    isAuth = true,
  ) {
    const { searchText, fromDate, toDate, conceptID } = params;

    try {
      /* Validations */
      if (conceptID === undefined || conceptID === '') {
        showError('Invalid concept ID.');
        return [];
      }

      /* Filters */
      let or: Array<ModelReservationFilterInput | null> | null = [];
      let and: Array<ModelReservationFilterInput | null> | null = [];
      const filter: any = {
        deleted: { eq: '0' },
      };

      if (searchText) {
        or.push({ guestName: { contains: searchText.toLowerCase() } });
        or.push({ guestPhoneNumber: { contains: searchText.toLowerCase() } });
        or.push({ confirmationNumber: { contains: searchText.toLowerCase() } });
      }

      if (or.length > 0) {
        filter.or = or;
      }

      // Date Filter
      let dateFilter = {};
      if (fromDate && toDate) {
        dateFilter = {
          between: [fromDate, toDate],
        };
      }

      let allItems: any[] = [];
      let nextToken: string | null = null;

      do {
        const response: any = await client.graphql({
          query: reservationsByConceptId,
          variables: {
            conceptId: conceptID,
            createdAt: dateFilter,
            filter,
            limit: 1000, // Amplify safe limit
            nextToken,
          },
          authMode: isAuth ? 'userPool' : 'iam',
        });

        const result = response.data.reservationsByConceptId;

        allItems = [...allItems, ...(result.items || [])];
        nextToken = result.nextToken ?? null;
      } while (nextToken);

      dispatch(setListing(allItems));
      return allItems;
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message || err);
      return [];
    }
  }

  async function get(params: ConceptGetVariables) {
    const { id, listing } = params;

    try {
      let single: any | undefined;
      if (listing.length > 0) {
        single = listing.find((resource: any) => resource.id === id);
      } else {
        const reservation: any = await client.graphql({
          query: getReservation,
          variables: { id },
          authMode: 'userPool',
        });
        single = reservation.data.getReservation;
      }

      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function create(params: CreateVariables) {
    const { userID, userName, data } = params;
    // Handle both conceptID (from form) and conceptId (expected format)
    const conceptId = data.conceptId || data.conceptID;
    if (!conceptId) {
      const error = new Error(`Cannot create ${singleName} without conceptId`);
      return showError(error);
    }

    if (!data.operaHotelId) {
      const error = new Error(
        `Cannot create ${singleName} without operaHotelId`,
      );
      return showError(error);
    }

    try {
      const createInput: any = {
        conceptId: conceptId,
        operaHotelId: data.operaHotelId,
        guestName: data.guestName || null,
        guestPhoneNumber: data.guestPhoneNumber || null,
        confirmationNumber: data.confirmationNumber || null,
        eventPrimaryKey: data.eventPrimaryKey || null,
        status: data.status || null,
        arrivalDate: data.arrivalDate || null,
        departureDate: data.departureDate || null,
        deleted: '0',
        createdAt: new Date().toISOString(),
        createdByID: userID,
        createdByName: userName,
      };

      const result = await client.graphql({
        query: createReservation,
        variables: { input: createInput },
        authMode: 'userPool',
      });

      // Refresh the listing to include the new item
      await fetch(
        {
          accountID: '',
          conceptID: conceptId,
          searchText: '',
          startIndex: 0,
          limit: 1000,
          moveForward: true,
        },
        true,
      );

      showConfirm(`New ${singleName} has been created successfully`);
      return true;
    } catch (err) {
      showError(err);
      return false;
    }
  }

  async function update(params: any) {
    const { id, listing, data } = params;

    try {
      const original: any = await get({ id, listing });

      if (!original) {
        showError(`Invalid ${singleName} ID`);
        return;
      }

      const updateInput: any = {
        id: original.id,
        conceptId:
          data.conceptId !== undefined ? data.conceptId : original.conceptId,
        operaHotelId:
          data.operaHotelId !== undefined
            ? data.operaHotelId
            : original.operaHotelId,
        guestName:
          data.guestName !== undefined ? data.guestName : original.guestName,
        guestPhoneNumber:
          data.guestPhoneNumber !== undefined
            ? data.guestPhoneNumber
            : original.guestPhoneNumber,
        confirmationNumber:
          data.confirmationNumber !== undefined
            ? data.confirmationNumber
            : original.confirmationNumber,
        eventPrimaryKey:
          data.eventPrimaryKey !== undefined
            ? data.eventPrimaryKey
            : original.eventPrimaryKey,
        status: data.status !== undefined ? data.status : original.status,
        arrivalDate:
          data.arrivalDate !== undefined
            ? data.arrivalDate
            : original.arrivalDate,
        departureDate:
          data.departureDate !== undefined
            ? data.departureDate
            : original.departureDate,
      };

      await client.graphql({
        query: updateReservation,
        variables: { input: updateInput },
        authMode: 'userPool',
      });

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function trash(params: ConceptGetVariables) {
    try {
      const { id, listing } = params;

      const original: any = await get(params);

      if (original) {
        const updateInput: any = {
          id: original.id,
          deleted: '1',
        };

        await client.graphql({
          query: updateReservation,
          variables: { input: updateInput },
          authMode: 'userPool',
        });

        dispatch(
          setListing(listing.filter((resource: any) => resource.id !== id)),
        );
      }

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function remove(params: ConceptGetVariables) {
    const { id, listing } = params;
    try {
      await client.graphql({
        query: deleteReservation,
        variables: { input: { id } },
        authMode: 'userPool',
      });

      dispatch(
        setListing(listing.filter((resource: any) => resource.id !== id)),
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(err);
    }
  }

  async function exportAll(params: ConceptListingVariables) {
    const data = await fetch(params);
    return data;
  }

  const getName = (params: ConceptGetVariables) => {
    const { id, listing } = params;

    if (listing.length > 0) {
      const model = listing.find((model: any) => model.id === id);
      return model ? model.guestName || model.confirmationNumber || '' : '';
    }

    return '';
  };

  function options(listing: any[]) {
    const options: Option[] = [];

    for (let option of listing) {
      options.push({
        label: `${
          option.guestName || option.confirmationNumber || 'Reservation'
        } - ${option.status || 'N/A'}`,
        value: option.id,
      });
    }

    return options;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: 'guestName',
      numeric: false,
      disablePadding: false,
      label: 'Guest Name',
    },
    {
      id: 'confirmationNumber',
      numeric: false,
      disablePadding: false,
      label: 'Confirmation Number',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: 'Status',
    },
    {
      id: 'arrivalDate',
      numeric: false,
      disablePadding: false,
      label: 'Arrival Date',
    },
    {
      id: 'departureDate',
      numeric: false,
      disablePadding: false,
      label: 'Departure Date',
    },
    {
      id: 'createdAt',
      numeric: false,
      disablePadding: false,
      label: 'Created At',
    },
    {
      id: 'actions',
      numeric: true,
      disablePadding: false,
      label: '',
    },
  ];

  const dataCells: readonly string[] = [
    'guestName',
    'confirmationNumber',
    'status',
    'arrivalDate',
    'departureDate',
    'createdAt',
  ];

  const api: any = {};

  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Options`] = options;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchByConept`] = fetchByConept;
  api[`${listingName}FetchAll`] = fetchAll;
  api[`${listingName}FetchAllByConept`] = fetchAllByConept;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}GetName`] = getName;
  api[`${listingName}ChangeListing`] = (listing: any[]) => {
    dispatch(setListing(listing));
    dispatch(setFilters(listing.map((model: any) => model.status)));
  };

  api[`${listingName}ChangeSelected`] = (id: string) => {
    dispatch(setSelected(id));
  };
  api[`${listingName}ChangeFilters`] = (filters: string[]) =>
    dispatch(setFilters(filters));
  api[`${listingName}ChangeSelectedFilters`] = (filters: any) => {
    dispatch(setSelectedFilters(filters));
  };
  api[`${listingName}NextToken`] = nextToken;
  api[`${listingName}Listing`] = listing;
  api[`${listingName}ClearListing`] = () => dispatch(setListing([]));
  api[`${listingName}ClearNextToken`] = () => dispatch(setNextToken(null));
  api[`${listingName}PrevNextToken`] = undefined;

  return api;
};

export default useReservation;
